import { RECREATE_ON_PARAM_CHANGE } from './core/routing/recreate-on-param-change.strategy';
import { Routes } from '@angular/router';
import { AuthGuardService } from './core/guards/auth-guard.service';
import { ViewerGuardService } from './core/guards/viewer-guard.guard';
import { AdminGuardService } from './core/guards/admin-guard.service';
import { SubscriptionGuardService } from './core/guards/subscription-guard.service';
import { capabilityGuard } from './core/guards/capability.guard';
import { coldCallsGuard } from './core/guards/cold-calls.guard';
import { inject } from '@angular/core';
import { GoogleCallbackService } from './features/auth/services/google-callback.service';
import { ActivatedRouteSnapshot, Route, UrlSegment } from '@angular/router';
import { permissionsSetupGuard } from './core/guards/permissions-setup.guard';
import { managerDashboardGuard } from './core/guards/manager-dashboard.guard';
import { practiceCoursesGuard } from './core/guards/practice-courses.guard';
import { redesignGuard } from './core/guards/redesign.guard';
import { knowledgeBaseEntryGuard, onboardingGateGuard } from './core/guards/onboarding-gate.guard';
import { roleplayV2Guard } from './core/guards/roleplay-v2.guard';
import { openSessionRedirectGuard } from './core/guards/open-session-redirect.guard';
import { organisationIdResolver } from './core/resolvers/organisation-id.resolver';
import { isProfileSection } from './features/admin/components/user-settings/user-settings.sections';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'main',
  },

  {
    path: 'pricing',
    loadComponent: () => import('./shared/ui/pricing-table/pricing-table.component').then(m => m.PricingTableComponent),
  },
  {
    path: 'checkout/success',
    loadComponent: () => import('./features/signup/components/checkout-success/checkout-success.component').then(m => m.CheckoutSuccessComponent),
  },
  {
    path: 'permissions-setup',
    loadComponent: () => import('./features/admin/components/permissions-setup/permissions-setup.component').then(m => m.PermissionsSetupComponent),
  },
  {
    path: 'login',
    canActivate: [permissionsSetupGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./features/auth/components/auth/auth.component').then(m => m.AuthComponent),
      },
      {
        path: 'verify-2fa',
        loadComponent: () =>
          import('./features/auth/components/auth/verify-two-factor/verify-two-factor.component').then(m => m.VerifyTwoFactorComponent),
      },
    ],
  },
  {
    path: 'registration',
    canActivate: [permissionsSetupGuard],
    loadComponent: () => import('./features/auth/components/auth/auth.component').then(m => m.AuthComponent),
  },
  {
    path: 'auth/setup-company',
    loadComponent: () => import('./features/auth/components/auth/auth.component').then(m => m.AuthComponent),
  },
  {
    path: 'auth',
    children: [
      {
        path: 'google/callback',
        canActivate: [
          (route: ActivatedRouteSnapshot) => {
            inject(GoogleCallbackService).handleCallback(route.queryParamMap);
            return false;
          },
        ],
        children: [],
      },
    ],
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./features/auth/components/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./features/auth/components/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
  },
  {
    path: 'terms-and-conditions',
    loadComponent: () =>
      import('./features/misc/components/terms-and-conditions/terms-and-conditions.component').then(m => m.TermsAndConditionsComponent),
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./features/misc/components/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent),
  },
  {
    path: 'faq',
    loadComponent: () => import('./features/faq/components/faq/faq.component').then(m => m.FaqComponent),
  },
  {
    path: 'main',
    loadComponent: () => import('./features/dashboard/components/main/main.component').then(m => m.MainComponent),
    canActivate: [AuthGuardService],
    canActivateChild: [onboardingGateGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
      },
      {
        path: 'dashboard',
        canActivate: [AuthGuardService, openSessionRedirectGuard],
        loadComponent: () => import('./features/dashboard/components/dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        // Manager view onto a teammate's personal dashboard (gated by managerDashboardGuard).
        path: 'dashboard/team/:userId',
        canActivate: [AuthGuardService, managerDashboardGuard],
        loadComponent: () => import('./features/dashboard/components/dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        path: 'manager-dashboard',
        canActivate: [AuthGuardService, managerDashboardGuard],
        loadComponent: () =>
          import('./features/manager-dashboard/components/manager-dashboard/manager-dashboard.component').then(m => m.ManagerDashboardComponent),
      },
      {
        // One shell, four children. The shell stays mounted across tab switches, so moving between
        // Overview, Assign and Courses no longer tears the workspace down and refetches everything.
        // `organisationId` reaches each child as an input through withComponentInputBinding().
        path: 'practice-courses',
        canActivate: [AuthGuardService, practiceCoursesGuard],
        loadComponent: () =>
          import('./features/practice-course/components/practice-course-workspace/practice-course-workspace.component').then(
            m => m.PracticeCourseWorkspaceComponent
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            resolve: { organisationId: organisationIdResolver },
            loadComponent: () =>
              import('./features/manager-dashboard/components/practice-course-dashboard/practice-course-dashboard.component').then(
                m => m.PracticeCourseDashboardComponent
              ),
            data: { showHeader: false },
          },
          {
            // The redesign's Insights tab: the same dashboard showing coverage, weaknesses and the funnel.
            path: 'insights',
            canMatch: [redesignGuard('/main/practice-courses')],
            resolve: { organisationId: organisationIdResolver },
            loadComponent: () =>
              import('./features/manager-dashboard/components/practice-course-dashboard/practice-course-dashboard.component').then(
                m => m.PracticeCourseDashboardComponent
              ),
            data: { showHeader: false, view: 'insights' },
          },
          {
            path: 'assign',
            resolve: { organisationId: organisationIdResolver },
            loadComponent: () =>
              import('./features/practice-course/components/course-assignment-workspace/course-assignment-workspace.component').then(
                m => m.CourseAssignmentWorkspaceComponent
              ),
          },
          {
            path: 'courses',
            resolve: { organisationId: organisationIdResolver },
            loadComponent: () => import('./features/practice-course/components/course-list/course-list.component').then(m => m.CourseListComponent),
          },
          {
            path: 'courses/:courseId',
            resolve: { organisationId: organisationIdResolver },
            loadComponent: () =>
              import('./features/practice-course/components/course-editor/course-editor.component').then(m => m.CourseEditorComponent),
          },
          {
            // The redesign's Scripts tab. Only the redesigned workspace links here; the legacy app keeps the scripts dialog.
            path: 'scripts',
            canMatch: [redesignGuard('/main/practice-courses')],
            loadComponent: () => import('./features/practice/roleplay-v2/scripts-page/scripts-page.component').then(m => m.ScriptsPageComponent),
          },
          {
            // `new` opens an unsaved script; any other id loads that script.
            path: 'scripts/:scriptId',
            canMatch: [redesignGuard('/main/practice-courses')],
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () => import('./features/practice/roleplay-v2/scripts-page/script-page.component').then(m => m.ScriptPageComponent),
          },
        ],
      },
      {
        // While the workspace is being built this is the screen the user lives on; once it is
        // ready the guard sends every link here on to the settings section instead.
        path: 'knowledge-base',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard, knowledgeBaseEntryGuard],
        loadComponent: () =>
          import('./features/knowledge-base/components/knowledge-base/knowledge-base.component').then(m => m.KnowledgeBaseComponent),
        data: { showSubBadges: false, showBackButton: false, requiredCapability: 'knowledgeBase' },
      },
      {
        path: 'create-new-document/create-sales-playbook',
        canActivate: [ViewerGuardService, AuthGuardService, SubscriptionGuardService],
        loadComponent: () =>
          import('./features/document-creation/components/create-sales-playbook/create-sales-playbook.component').then(
            m => m.CreateSalesPlaybookComponent
          ),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'create-new-document/create-ideal-customer-profile',
        canActivate: [ViewerGuardService, AuthGuardService, SubscriptionGuardService],
        loadComponent: () =>
          import('./features/document-creation/components/create-ideal-customer-profile/create-ideal-customer-profile.component').then(
            m => m.CreateIdealCustomerProfileComponent
          ),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'create-new-document/create-qualifying-questions',
        canActivate: [ViewerGuardService, AuthGuardService, SubscriptionGuardService],
        loadComponent: () =>
          import('./features/document-creation/components/create-qualifying-questions/create-qualifying-questions.component').then(
            m => m.CreateQualifyingQuestionsComponent
          ),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'create-new-document/create-openers',
        // Openers is a Cold Calls document: the same flag and module as the Cold Calls routes.
        canActivate: [ViewerGuardService, AuthGuardService, SubscriptionGuardService, capabilityGuard, coldCallsGuard],
        loadComponent: () =>
          import('./features/document-creation/components/create-openers/create-openers.component').then(m => m.CreateOpenersComponent),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'coldCallAssistant' },
      },
      {
        path: 'create-new-document/create-product-pricing-guide',
        canActivate: [ViewerGuardService, AuthGuardService, SubscriptionGuardService],
        loadComponent: () =>
          import('./features/document-creation/components/create-product-pricing-guide/create-product-pricing-guide.component').then(
            m => m.CreateProductPricingGuideComponent
          ),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'create-new-document/import-document',
        canActivate: [ViewerGuardService, AuthGuardService, SubscriptionGuardService],
        loadComponent: () =>
          import('./features/document-creation/components/import-document/import-document.component').then(m => m.ImportDocumentComponent),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'create-new-document/import-website',
        canActivate: [ViewerGuardService, AuthGuardService, SubscriptionGuardService],
        loadComponent: () =>
          import('./features/document-creation/components/import-website/import-website.component').then(m => m.ImportWebsiteComponent),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'create-new-document/create-document',
        canActivate: [ViewerGuardService, AuthGuardService, SubscriptionGuardService],
        loadComponent: () =>
          import('./features/document-creation/components/create-document/create-document.component').then(m => m.CreateDocumentComponent),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'knowledge-base/:id',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('./features/knowledge-base/components/document/document.component').then(m => m.DocumentComponent),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'knowledgeBase' },
      },
      {
        // Roleplay V2. Matches only when the flag is on; otherwise the legacy route below takes over.
        path: 'playground',
        canMatch: [roleplayV2Guard],
        canActivate: [AuthGuardService, SubscriptionGuardService],
        loadComponent: () =>
          import('./features/practice/roleplay-v2/practice-home-v2/practice-home-v2.component').then(m => m.PracticeHomeV2Component),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        // Roleplay V2 Practice history (UIRedesign). V2 only, with no legacy route behind it: nothing
        // links here with the flag off; the home's "All" link opens it.
        path: 'playground/history',
        canMatch: [roleplayV2Guard, redesignGuard('/main/playground')],
        canActivate: [AuthGuardService, SubscriptionGuardService],
        loadComponent: () => import('./features/practice/roleplay-v2/history-page/history-page.component').then(m => m.PracticeHistoryPageComponent),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        // Roleplay V2 Progress (UIRedesign). V2 only, like History: History's "Progress" link opens it.
        path: 'playground/progress',
        canMatch: [roleplayV2Guard, redesignGuard('/main/playground')],
        canActivate: [AuthGuardService, SubscriptionGuardService],
        loadComponent: () =>
          import('./features/practice/roleplay-v2/progress-page/progress-page.component').then(m => m.PracticeProgressPageComponent),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'playground',
        canActivate: [AuthGuardService, SubscriptionGuardService],
        loadComponent: () => import('./features/practice/components/playground/playground.component').then(m => m.PlaygroundComponent),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'playground/customer-email-practice',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () =>
          import('./features/practice/components/customer-email-practice/setup/customer-email-practice-setup.component').then(
            m => m.CustomerEmailPracticeSetupComponent
          ),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'emailPractice' },
      },
      {
        path: 'playground/customer-email-practice/:sessionId',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () =>
          import('./features/practice/components/customer-email-practice/customer-email-practice-route.component').then(
            m => m.CustomerEmailPracticeRouteComponent
          ),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'emailPractice' },
      },
      {
        // Roleplay V2 live call. Matches only when the flag is on; otherwise the legacy route below
        // takes over, so flag off is today's setup screen, call and review, untouched.
        path: 'playground/customer-call-practice',
        canMatch: [roleplayV2Guard],
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () => import('./features/practice/roleplay-v2/live-call/live-call-v2.component').then(m => m.LiveCallV2Component),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'coldCallPractice' },
      },
      {
        path: 'playground/customer-call-practice',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () =>
          import('./features/practice/components/customer-call-practice/customer-call-practice.component').then(m => m.CustomerCallPracticeComponent),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'coldCallPractice' },
      },
      {
        // Roleplay V2 review (screen 6). V2 only: with the flag off nothing links here, and the
        // legacy review stays where it is, inside the legacy call practice component above.
        // No capability guard on purpose: a review is the rep's own history and outlives the capability.
        path: 'playground/review/:sessionId',
        canMatch: [roleplayV2Guard],
        canActivate: [AuthGuardService, SubscriptionGuardService],
        loadComponent: () => import('./features/practice/roleplay-v2/review/session-review-v2.component').then(m => m.SessionReviewV2Component),
        data: { showSubBadges: false, showBackButton: true },
      },
      {
        path: 'assistant',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () =>
          import('./features/live-assistant/components/live-assistant/live-assistant.component').then(m => m.LiveAssistantComponent),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'liveAssistant' },
      },
      {
        // A finished live call, reopened from Home, a deal room or Lizzie. Not under 'assistant': the
        // subscription guard gates that path on Assistant minutes, and a rep's past calls stay theirs.
        path: 'calls/:sessionId',
        canActivate: [AuthGuardService],
        loadComponent: () => import('./features/live-assistant/components/call-page/call-page.component').then(m => m.CallPageComponent),
      },
      {
        path: 'lizzie',
        canActivate: [AuthGuardService],
        loadComponent: () => import('./features/lizzie/components/lizzie-workspace/lizzie-workspace.component').then(m => m.LizzieWorkspaceComponent),
        // The chat on screen is a child segment rather than a route of its own, so going from a new chat to
        // its address, or between chats, keeps the page instead of rebuilding it mid-answer.
        children: [
          { path: '', children: [] },
          { path: ':sessionId', children: [] },
        ],
      },
      // Cold calls run on the dialler MVP for now; the script-based assistant is parked until it is folded in.
      {
        path: 'cold-calls',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard, coldCallsGuard],
        loadComponent: () => import('./features/dialer/components/dialer-lists/dialer-lists.component').then(m => m.DialerListsComponent),
        data: { showSubBadges: false, requiredCapability: 'coldCallAssistant' },
      },
      {
        path: 'cold-calls/lists/:listId',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard, coldCallsGuard],
        loadComponent: () => import('./features/dialer/components/dialer-list/dialer-list.component').then(m => m.DialerListComponent),
        data: { showSubBadges: false, requiredCapability: 'coldCallAssistant' },
      },
      {
        path: 'cold-calls/lists/:listId/complete',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard, coldCallsGuard],
        loadComponent: () =>
          import('./features/dialer/components/dialer-run-complete/dialer-run-complete.component').then(m => m.DialerRunCompleteComponent),
        data: { showSubBadges: false, requiredCapability: 'coldCallAssistant' },
      },
      {
        path: 'cold-calls/contacts/:contactId',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard, coldCallsGuard],
        loadComponent: () => import('./features/dialer/components/dialer-contact/dialer-contact.component').then(m => m.DialerContactComponent),
        data: { showSubBadges: false, requiredCapability: 'coldCallAssistant' },
      },
      {
        path: 'cold-calls/calls/:callId',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard, coldCallsGuard],
        loadComponent: () => import('./features/dialer/components/dialer-live-call/dialer-live-call.component').then(m => m.DialerLiveCallComponent),
        data: { showSubBadges: false, requiredCapability: 'coldCallAssistant' },
      },
      {
        path: 'cold-calls/calls/:callId/review',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard, coldCallsGuard],
        loadComponent: () =>
          import('./features/dialer/components/dialer-call-review/dialer-call-review.component').then(m => m.DialerCallReviewComponent),
        data: { showSubBadges: false, requiredCapability: 'coldCallAssistant' },
      },
      {
        path: 'cold-calls/scripts/new',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () =>
          import('./features/live-assistant/components/cold-call-assistant/script-editor/script-editor.component').then(m => m.ScriptEditorComponent),
        data: { requiredCapability: 'coldCallAssistant' },
      },
      {
        path: 'cold-calls/scripts/edit/:scriptId',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () =>
          import('./features/live-assistant/components/cold-call-assistant/script-editor/script-editor.component').then(m => m.ScriptEditorComponent),
        data: { requiredCapability: 'coldCallAssistant' },
      },
      {
        path: 'deal-rooms',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () =>
          import('./features/document-creation/components/persistence-documents/pd-list/pd-list.component').then(m => m.PdListComponent),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'dealRooms' },
      },
      {
        path: 'deal-rooms/:id',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () =>
          import('./features/document-creation/components/persistence-documents/pd-detail/pd-detail.component').then(m => m.PdDetailComponent),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'dealRooms', [RECREATE_ON_PARAM_CHANGE]: true },
      },
      {
        path: 'persistence-documents',
        pathMatch: 'full',
        redirectTo: 'deal-rooms',
      },
      {
        path: 'persistence-documents/:id',
        redirectTo: 'deal-rooms/:id',
      },
      {
        path: 'manage-organisations/:organisationId',
        canActivate: [AdminGuardService, AuthGuardService],
        loadComponent: () =>
          import('./features/admin/components/organisations/organisation-hub/organisation-hub.component').then(m => m.OrganisationHubComponent),
      },
      {
        path: 'manage-organisations/:organisationId/subscription-billing',
        canActivate: [AdminGuardService, AuthGuardService],
        loadComponent: () =>
          import('./features/admin/components/organisations/organisation-hub/subscription/subscription.component').then(m => m.SubscriptionComponent),
      },
      {
        // Same component as 'user-settings', but the knowledge-base section keeps the gates the
        // stand-alone knowledge base page had. The path still contains 'knowledge-base', which is
        // what SubscriptionGuardService matches on.
        path: 'user-settings/knowledge-base',
        canActivate: [AuthGuardService, SubscriptionGuardService, capabilityGuard],
        loadComponent: () => import('./features/admin/components/user-settings/user-settings.component').then(m => m.UserSettingsComponent),
        data: { showSubBadges: false, showBackButton: true, requiredCapability: 'knowledgeBase' },
      },
      {
        // An address naming a section that does not exist must not quietly render the profile:
        // refusing the match drops the URL through to the wildcard, which is this app's 404.
        path: 'user-settings/:section',
        canMatch: [(_route: Route, segments: UrlSegment[]) => isProfileSection(segments[1]?.path)],
        canActivate: [AuthGuardService],
        loadComponent: () => import('./features/admin/components/user-settings/user-settings.component').then(m => m.UserSettingsComponent),
      },
      {
        path: 'user-settings',
        canActivate: [AuthGuardService],
        loadComponent: () => import('./features/admin/components/user-settings/user-settings.component').then(m => m.UserSettingsComponent),
      },
      {
        path: 'faq',
        canActivate: [AuthGuardService],
        loadComponent: () => import('./features/faq/components/faq/faq.component').then(m => m.FaqComponent),
      },
      {
        path: 'feedback',
        canActivate: [AuthGuardService],
        loadComponent: () => import('./shared/ui/feedback/feedback.component').then(m => m.FeedbackComponent),
      },
      {
        path: 'faq',
        canActivate: [AuthGuardService],
        loadComponent: () => import('./features/faq/components/faq/faq.component').then(m => m.FaqComponent),
      },
      {
        path: 'subscription-required',
        loadComponent: () => import('./shared/ui/subscription-required/subscription-required.component').then(m => m.SubscriptionRequiredComponent),
      },
    ],
  },
  {
    path: 'assistant-pip',
    loadComponent: () =>
      import('./features/live-assistant/components/live-assistant/assistant-pip/assistant-pip.component').then(m => m.AssistantPipComponent),
  },
  {
    path: 'meeting-reminder-pip',
    loadComponent: () => import('./features/meeting-reminder/components/meeting-reminder-pip.component').then(m => m.MeetingReminderPipComponent),
  },
  {
    path: '**',
    redirectTo: 'main',
  },
];
